import { SafeHydrate } from "@/SafeHydrate";
import React, { useState, useEffect } from "react";
import { Menu, X, Phone, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, navigationMenuTriggerStyle } from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="1753" className="flex gap-4">
        {[1, 2, 3].map(i => <div key={i} data-index={i} className="h-9 w-20 bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList>
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild className={navigationMenuTriggerStyle()}>
              <Link to={item.href || "/BookViewing"} className="text-sm font-medium text-foreground/90 hover:text-primary transition-colors font-sans">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 p-4">
        {[1, 2, 3].map(i => <div key={i} data-index={i} className="h-8 w-full bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  return <nav className="flex flex-col gap-2 p-4">
      {menuItems.map((item, i) => <div key={item.id} data-index={i} className="border-b border-border/30 last:border-0">
          <Link to={item.href || "#"} className="block py-3 text-lg font-medium text-foreground hover:text-primary transition-colors" onClick={onClose}>
            {item.label}
          </Link>
        </div>)}
    </nav>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="header" className={cn("sticky top-0 z-50 w-full transition-all duration-300 border-b border-transparent", isScrolled ? "bg-background/80 backdrop-blur-xl border-border shadow-sm" : "bg-background")}>
      <div className="container mx-auto px-4 h-20 flex items-center justify-between">
        {}
        <div className="flex-shrink-0">
          <Link to="/HomePage" className="block">
            <img src="https://perfect-duck.10web.cloud/wp-content/uploads/2026/03/Coworking-Kassel-Logo.png" alt="BLUVIT GmbH - Coworking Space Kassel" className="h-8 w-auto object-contain" />
          </Link>
        </div>

        {}
        <div className="hidden lg:flex items-center gap-8">
          <MenuProvider menu_id="43" className="hidden lg:block">
            <DesktopMenuConsumer />
          </MenuProvider>

          <Button asChild className="font-semibold rounded-full bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm hover:shadow-md transition-all px-8">
            <Link to="/CoworkingSpacePage">Kontakt</Link>
          </Button>
        </div>

        {}
        <div className="lg:hidden flex items-center gap-4">
          <Button variant="ghost" size="icon" className="text-foreground" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu">
            {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
        </div>
      </div>

      {}
      {isMobileMenuOpen && <div className="lg:hidden absolute top-20 left-0 w-full bg-background border-b border-border shadow-xl animate-in slide-in-from-top-5 duration-200">
          <div className="container mx-auto py-4">
            <MenuProvider menu_id="43">
              <MobileMenuConsumer onClose={() => setIsMobileMenuOpen(false)} />
            </MenuProvider>
            <div className="p-4 pt-2 flex flex-col gap-3">
              <Button asChild className="w-full justify-center font-semibold rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                <Link to="#" onClick={() => setIsMobileMenuOpen(false)}>
                  <SafeHydrate><Calendar className="mr-2 h-4 w-4" /></SafeHydrate>
                  Probetag buchen
                </Link>
              </Button>
              <Button asChild variant="outline" className="w-full justify-center font-semibold rounded-full border-foreground/20 text-foreground hover:bg-foreground/5">
                <Link to="tel:+4956151090993">
                  <Phone className="mr-2 h-4 w-4" />
                  Fragen? 056151090993
                </Link>
              </Button>
            </div>
          </div>
        </div>}
    </header>;
}